import { html, SP } from '../../html.js';
import type { VNode } from '../../html.js';

/**
 * A capability whose binding cannot honour a parameter it advertises.
 *
 * This is the defect that had the live tenant answering "when is Stalker
 * showing?" with Vertigo's showtime. `get_film_details(film)` declared `film`
 * required with an enum of five slugs, while the binding's only filter clause
 * read `value: "{{query}}"` — a variable declared nowhere. `skipIfEmpty` turns an
 * unresolvable operand into a skipped clause, the next step is `limit n:1`, and
 * all five slugs return row one with `ok:true` and "1 result". There is no error
 * anywhere in that, which is precisely why it needs a place in the layout rather
 * than a tooltip: nothing about the tool's behaviour looks wrong, so the only
 * warning a person will ever get is this one.
 *
 * `bindingVarIssues` in @clawharbor/manifest produces the messages. They are
 * printed whole because each one names the input and the step, and a shortened
 * version would not tell the operator where to look.
 */
export function IssueBlock(props: { tool: string; messages: string[] }): VNode | null {
  if (!props.messages.length) return null;
  const n = props.messages.length;

  return html`
    <div class="issue" role="alert">
      <p class="issue__h">
        ${n === 1 ? 'This tool advertises a parameter it cannot honour' : `${n} binding-variable faults`}
      </p>
      <ul class="issue__list">
        ${props.messages.map((m) => html`<li class="issue__item">${m}</li>`)}
      </ul>
      <p class="issue__p">
        A parameter no template reads is inert: the clause that should filter on it is skipped, the
        call still returns rows, and the caller gets a confident answer to a different question.${SP}
        Fix the binding's template — <code>{{$input.${firstInput(props.messages) ?? 'name'}}}</code>${SP}
        is the form a row field cannot shadow — and republish. This cannot be corrected from this
        screen, because a binding is data the publish path validates rather than text.
      </p>
    </div>
  `;
}

/**
 * The input name out of the first message, for the example template.
 *
 * The checker writes the name in double quotes; pulling it out means the example
 * shows the operator's own parameter rather than a placeholder they have to
 * mentally substitute.
 */
function firstInput(messages: string[]): string | null {
  for (const m of messages) {
    const found = /"([a-z][a-z0-9_]*)"/i.exec(m);
    if (found) return found[1] ?? null;
  }
  return null;
}
